import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as EndIcon } from '@/assets/icons/stop-circle.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { BPTestingProjectResource } from '@/types/resources';
import { api } from '@/utils/api';
import { BPTestingProjectStatus } from '@/utils/enums';
import { Color } from '@/utils/theme';

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
  margin-bottom: 32px;
`;

const Time = styled(Text)`
  margin-top: 4px;
`;

const Uuid = styled(Text)`
  margin-top: 5px;
`;

const StatusHolder = styled.div`
  margin-bottom: 3px;
  display: flex;
  align-items: center;
`;

const StatusDot = styled.div<{ $color: Color }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${({ theme, $color }) => theme.colors[$color]};
`;

const StatusCount = styled(Text)`
  margin-left: auto;
`;

interface Props {
  project: BPTestingProjectResource;
}

const BPTestingProjectTableRow: FC<Props> = ({ project }) => {
  const { status } = useParams();

  const alerts = useAlerts();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const handleRowClick = (id: number) => {
    navigate(`/general/bp-testing-projects/${id}`);
  };

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this project?`,
      );

      if (!confirm) return;

      await api.delete(`/v1/editorial/bp-testing-projects/${project.id}`);

      await queryClient.invalidateQueries(['bp_testing_projects']);

      alerts.success('Project was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const onProjectEnd = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to end this project?`,
      );

      if (!confirm) return;

      await api.put(`/v1/editorial/bp-testing-projects/${project.id}`, {
        status: BPTestingProjectStatus.ENDED,
      });

      await queryClient.invalidateQueries(['bp_testing_projects']);

      alerts.success('Project has been successfully ended');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <Tr onClick={() => handleRowClick(project.id)}>
      <Td>
        {project.name}
        <br />
        <Uuid $color="gray" $type="extraSmall">
          {project.uuid}
        </Uuid>
      </Td>
      <Td>
        {DateTime.fromISO(project.createdAt).toLocaleString()} <br />
        <Time $color="gray" $type="extraSmall">
          {DateTime.fromISO(project.createdAt).toFormat('HH:mm')}
        </Time>
      </Td>
      <Td>{project.owner}</Td>
      <Td>
        <StatusHolder>
          <StatusDot $color="warning" />
          <Text as="span" $type="smallHeadline">
            Processing:
          </Text>
          <StatusCount as="span" $type="smallHeadline" $color="gray">
            {project.processingTests ?? 0}
          </StatusCount>
        </StatusHolder>
        <StatusHolder>
          <StatusDot $color="success" />
          <Text as="span" $type="smallHeadline">
            Active:
          </Text>
          <StatusCount as="span" $type="smallHeadline" $color="gray">
            {project.activeTests ?? 0}
          </StatusCount>
        </StatusHolder>
        <StatusHolder>
          <StatusDot $color="error" />
          <Text as="span" $type="smallHeadline">
            Failed:
          </Text>
          <StatusCount as="span" $type="smallHeadline" $color="gray">
            {project.failedTests ?? 0}
          </StatusCount>
        </StatusHolder>
        <StatusHolder>
          <StatusDot $color="gray" />
          <Text as="span" $type="smallHeadline">
            Done:
          </Text>
          <StatusCount as="span" $type="smallHeadline" $color="gray">
            {project.endedTests ?? 0}
          </StatusCount>
        </StatusHolder>
      </Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            {status !== 'ended' && (
              <>
                <ActionsDropdownButton as="button" onClick={onProjectEnd}>
                  <span>
                    <EndIcon />
                  </span>
                  Stop
                </ActionsDropdownButton>
              </>
            )}
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default BPTestingProjectTableRow;
